import React from "react";
import { parse, MarkedOptions, Renderer } from "marked";
import {
	DataObserveKey,
	VisualComponent,
	ExternalHtml,
	useTranslations,
} from "@siteimprove/fancylib";

export type MarkdownProps = {
	children: string;
	options?: MarkedOptions;
	className?: string;
	noEscape?: boolean;
	openLinksInNewTab?: boolean;
} & DataObserveKey &
	VisualComponent;

const defaultOptions: MarkedOptions = {
	gfm: false,
	breaks: false,
	pedantic: false,
	smartLists: true,
	smartypants: false,
};

export function Markdown(props: MarkdownProps): JSX.Element {
	const { children, className, noEscape, openLinksInNewTab, ...rest } = props;
	let prepped = children;
	const i18n = useTranslations();

	if (noEscape !== true) {
		prepped = prepped
			.replace(/</g, "&lt;") // Escape <
			.replace(/([^>\n])>/, "$1&gt;"); // Escape > that are not the first character of a line
	}

	let options = props.options;
	const renderer = new Renderer();

	if (openLinksInNewTab) {
		const linkRenderer = renderer.link;
		renderer.link = (href: string, title: string, text: string) => {
			const htmlResult = linkRenderer.call(renderer, href, title, text);
			return htmlResult.replace(
				/^<a /,
				`<a target="_blank" rel="noopener" aria-label="${i18n.linkButtonOpenNewAriaLabel}: ${text}" `
			);
		};
	}

	const codespanRenderer = renderer.codespan;
	renderer.codespan = (code: string) => {
		const codespanResult = codespanRenderer.call(renderer, code);
		return replaceAngleBracketsInCode(codespanResult);
	};

	const codeRenderer = renderer.code;
	renderer.code = (code: string, language: string, isEscaped: boolean) => {
		const codeResult = codeRenderer.call(renderer, code, language, isEscaped);
		return replaceAngleBracketsInCode(codeResult);
	};

	options = { ...options, renderer: renderer };

	return (
		<ExternalHtml {...rest} data-component="markdown" className={className}>
			{parse(prepped, { ...defaultOptions, ...options })}
		</ExternalHtml>
	);
}

function replaceAngleBracketsInCode(code: string) {
	return code.replace(/&amp;lt;/g, "&lt;").replace(/&amp;gt;/g, "&gt;");
}
